import * as THREE from "three";
import { weatherGroundFromClimate, surfaceColumn, createSurfaceColumn, type TerrainSample } from "@hyperlight/shared";
import type { Planet } from "../planet";
import { WEATHER } from "./weatherField";

const N = 32, CELL = 4;
const climate: TerrainSample = { h: 0, temp: 0, moist: 0, river: 0, mountain: 0, flowx: 0, flowy: 0, flowz: 0, town: 0, road: 8 };
const surface = createSurfaceColumn();
const ground = { wet: 0, snow: 0 };
interface Cell { stop: number; terrain: number; wet: number; snow: number; time: number; history: number; temp: number; moist: number }

/** A small terrain/roof/water + aftermath cache. Planet-local, so camera motion
 * and floating-origin shifts never move its contents. Budgeted rebuilds avoid a
 * terrain/history spike when moving into a new area. Unknown cells reject drops. */
export class WeatherSurface {
  private readonly pixels = new Float32Array(N * N * 4);
  readonly texture = new THREE.DataTexture(this.pixels, N, N, THREE.RGBAFormat, THREE.FloatType);
  readonly uniforms = {
    uWxSurface: { value: this.texture as THREE.Texture },
    uWxAnchor: { value: new THREE.Vector3() },
    uWxA: { value: new THREE.Vector3(1, 0, 0) },
    uWxB: { value: new THREE.Vector3(0, 0, 1) },
    uWxGrid: { value: new THREE.Vector2() },
    uWxSurfaceOn: { value: 0 },
    uWxRadius: { value: 0 },
    uWxInvPlanet: { value: new THREE.Matrix4() },
  };
  private planet: Planet | null = null;
  private readonly cells = new Map<string, Cell>();
  private readonly point = new THREE.Vector3();
  private readonly up = new THREE.Vector3();
  private cursor = 0;
  private preset = WEATHER.preset;

  constructor() { this.texture.minFilter = this.texture.magFilter = THREE.NearestFilter; this.texture.generateMipmaps = false; }

  update(planet: Planet, local: THREE.Vector3, time: number,
    roof?: (x: number, y: number, z: number) => number | null): void {
    const u = this.uniforms;
    let dirty = false;
    if (this.planet !== planet || local.distanceToSquared(u.uWxAnchor.value) > 240 * 240 || this.preset !== WEATHER.preset) {
      this.planet = planet;
      this.cells.clear(); this.cursor = 0; this.preset = WEATHER.preset;
      dirty = true;
      u.uWxAnchor.value.copy(local);
      this.up.copy(local).normalize();
      u.uWxA.value.set(Math.abs(this.up.x) > 0.9 ? 0 : 1, Math.abs(this.up.x) > 0.9 ? 1 : 0, 0).cross(this.up).normalize();
      u.uWxB.value.crossVectors(this.up, u.uWxA.value).normalize();
    }
    this.point.copy(local).sub(u.uWxAnchor.value);
    const gx = Math.floor(this.point.dot(u.uWxA.value) / CELL) - N / 2;
    const gy = Math.floor(this.point.dot(u.uWxB.value) / CELL) - N / 2;
    dirty ||= u.uWxGrid.value.x !== gx || u.uWxGrid.value.y !== gy;
    u.uWxGrid.value.set(gx, gy);
    u.uWxRadius.value = planet.def.radius;
    planet.group.updateWorldMatrix(true, false);
    u.uWxInvPlanet.value.copy(planet.group.matrixWorld).invert();
    // Terrain/exposure can fill quickly; the more expensive backward weather
    // integral has its own four-cell budget. No per-particle terrain queries.
    let historyBudget = 4;
    for (let budget = 0; budget < 16; budget++) {
      const index = this.cursor++ % (N * N);
      const x = (index % N + N / 2) % N + gx, y = (Math.floor(index / N) + N / 2) % N + gy;
      const key = `${x},${y}`;
      let cell = this.cells.get(key);
      const terrainDue = !cell || time < cell.time || time - cell.time >= 8;
      const historyDue = !cell || time < cell.history || time - cell.history >= 8;
      if (!terrainDue && (!historyDue || historyBudget <= 0)) continue;
      dirty = true;
      this.point.copy(u.uWxAnchor.value).addScaledVector(u.uWxA.value, (x + 0.5) * CELL).addScaledVector(u.uWxB.value, (y + 0.5) * CELL).normalize();
      const d = this.point;
      if (terrainDue) {
        surfaceColumn(planet.sampler, planet.def, d.x, d.y, d.z, surface);
        planet.sampler.sample(d.x, d.y, d.z, climate, true);
        let stop = planet.settlementColliders?.clearanceRadius(d.x, d.y, d.z, surface.topR, CELL * 0.7) ?? surface.topR;
        stop = Math.max(stop, roof?.(d.x * surface.topR, d.y * surface.topR, d.z * surface.topR) ?? -Infinity);
        cell = { stop: stop - planet.def.radius, terrain: surface.supportR - planet.def.radius,
          wet: cell?.wet ?? 0, snow: cell?.snow ?? 0, time, history: cell?.history ?? -Infinity,
          temp: climate.temp, moist: climate.moist };
        this.cells.set(key, cell);
      }
      if (WEATHER.preset) {
        cell!.wet = WEATHER.preset === "rain" ? 0.7 : 0;
        cell!.snow = WEATHER.preset === "snow" ? 0.6 : 0;
        cell!.history = time;
      } else if (historyDue && historyBudget > 0) {
        historyBudget--;
        weatherGroundFromClimate(planet.def, d.x, d.y, d.z, time, cell!.temp, cell!.moist, ground);
        cell!.wet = ground.wet; cell!.snow = ground.snow; cell!.history = time;
      }
    }
    if (dirty) {
      const data = this.pixels;
      for (let y = 0; y < N; y++) for (let x = 0; x < N; x++) {
        const cell = this.cells.get(`${x + gx},${y + gy}`), i = (y * N + x) * 4;
        data[i] = cell?.stop ?? 1e6;
        data[i + 1] = cell?.terrain ?? -1e6;
        data[i + 2] = cell?.wet ?? 0;
        data[i + 3] = cell?.snow ?? 0;
      }
      this.texture.needsUpdate = true;
    }
    if (this.cells.size > N * N * 3) {
      for (const key of this.cells.keys()) {
        const [x, y] = key.split(",").map(Number);
        if (x < gx || x >= gx + N || y < gy || y >= gy + N) this.cells.delete(key);
      }
    }
    u.uWxSurfaceOn.value = 1;
  }
  disable(): void { this.uniforms.uWxSurfaceOn.value = 0; }
  dispose(): void { this.texture.dispose(); this.cells.clear(); }
}

export const WEATHER_SURFACE_GLSL = /* glsl */ `
uniform sampler2D uWxSurface;
uniform vec3 uWxAnchor, uWxA, uWxB;
uniform vec2 uWxGrid;
uniform float uWxSurfaceOn, uWxRadius;
uniform mat4 uWxInvPlanet;
vec2 wxSurfaceUv(vec3 local) {
  vec3 d = local - uWxAnchor;
  return (vec2(dot(d, uWxA), dot(d, uWxB)) / ${CELL.toFixed(1)} - uWxGrid) / ${N.toFixed(1)};
}
float wxSurfaceInside(vec2 uv) { return step(0.0, min(uv.x, uv.y)) * step(max(uv.x, uv.y), 1.0) * uWxSurfaceOn; }
`;
